/**
 * Hook to manage navigation guard state across settings pages
 * 
 * This hook allows child pages (like AccountSettingsPage) to register
 * their guard state, which is then provided to SettingsNav via context
 */

import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Global state to track guard state from child pages
let globalGuardState: {
  hasUnsavedChanges: boolean;
  onNavigationAttempt: ((path: string) => void) | null;
} = {
  hasUnsavedChanges: false,
  onNavigationAttempt: null,
};

// Function to update guard state (called by child pages)
export function setNavigationGuardState(
  hasUnsavedChanges: boolean,
  onNavigationAttempt: ((path: string) => void) | null
): void {
  globalGuardState = {
    hasUnsavedChanges,
    onNavigationAttempt,
  };
  // Trigger update for any listeners
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('navigationGuardStateChanged'));
  }
}

export function useNavigationGuardState(): {
  hasUnsavedChanges: boolean;
  onNavigationAttempt: (path: string) => void;
} {
  const location = useLocation();
  const [state, setState] = useState(globalGuardState);

  useEffect(() => {
    const handleStateChange = () => {
      setState({ ...globalGuardState });
    };

    window.addEventListener('navigationGuardStateChanged', handleStateChange);
    // Also check on location change
    setState({ ...globalGuardState });

    return () => {
      window.removeEventListener('navigationGuardStateChanged', handleStateChange);
    };
  }, [location.pathname]);

  const onNavigationAttempt = useCallback((path: string) => {
    if (globalGuardState.onNavigationAttempt) {
      globalGuardState.onNavigationAttempt(path);
    }
  }, []);

  return {
    hasUnsavedChanges: state.hasUnsavedChanges,
    onNavigationAttempt,
  };
}

